import { Head, router } from '@inertiajs/react'
import { useState } from 'react'
import { HiOutlineTrash } from 'react-icons/hi2'
import AppLayout from '../../components/AppLayout'
import ConfirmDialog from '../../components/ConfirmDialog'
import PageSubHeader from '../../components/PageSubHeader'
import UserForm, { UserFormValues } from '../../components/UserForm'

type FormUser = UserFormValues & { id: number }

type Props = {
  user: FormUser
  errors: Record<string, string[] | undefined>
  can_change_role: boolean
  can_delete: boolean
}

export default function EditUser({ user, errors, can_change_role, can_delete }: Props) {
  const [processing, setProcessing] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const handleSubmit = (values: UserFormValues) => {
    setProcessing(true)
    router.patch(`/users/${user.id}`, { user: values }, {
      onFinish: () => setProcessing(false),
    })
  }

  const handleDelete = () => {
    router.delete(`/users/${user.id}`, {
      onFinish: () => setConfirmingDelete(false),
    })
  }

  return (
    <>
      <Head title="Edit User" />
      <AppLayout
        active="users"
        hideTabs
        subHeader={
          <PageSubHeader backHref="/users" backLabel="Back to users" title="Edit User Details" />
        }
      >

        <UserForm
          initialValues={{
            name: user.name,
            email: user.email,
            phone: user.phone,
            role: user.role,
          }}
          errors={errors}
          submitLabel="Save Changes"
          cancelHref="/users"
          onSubmit={handleSubmit}
          processing={processing}
          roleDisabled={!can_change_role}
          leftAction={
            can_delete ? (
              <button
                type="button"
                onClick={() => setConfirmingDelete(true)}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-5 py-3 font-display text-base font-medium text-ink transition hover:bg-rose-50 hover:text-rose-600"
              >
                <HiOutlineTrash className="h-5 w-5" />
                Delete User
              </button>
            ) : null
          }
        />
      </AppLayout>

      <ConfirmDialog
        open={confirmingDelete}
        tone="danger"
        title="Delete this user?"
        body={
          <>
            You're about to delete <strong>{user.name || user.email}</strong>. Any leads
            currently assigned to them will become unassigned. This cannot be undone.
          </>
        }
        confirmLabel="Delete user"
        onConfirm={handleDelete}
        onClose={() => setConfirmingDelete(false)}
      />
    </>
  )
}
